import { forkJoin, map, mergeMap } from "rxjs";
import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';

@Injectable()
export class TrainingSharedFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  currentAvailableSlides$ = this.store.select(FlatStates.TrainingSharedState.currentAvailableSlides$);
  selectedNodesSet$ = this.store.select(FlatStates.TrainingSharedState.selectedNodesSet$);
  selectedSlideNodes$ = this.store.select(FlatStates.TrainingSharedState.selectedSlideNodes$);
  currentSlideResultShowFeedback$ = this.store.select(FlatStates.TrainingSharedState.currentSlideResultShowFeedback$);
  currentSlideResultShowExplanation$ = this.store.select(FlatStates.TrainingSharedState.currentSlideResultShowExplanation$);
  selectedEmptyPercentageSlideResults$ = this.store.select(FlatStates.TrainingSharedState.selectedEmptyPercentageSlideResults$);
  selectedCorrectPercentageSlideResults$ = this.store.select(FlatStates.TrainingSharedState.selectedCorrectPercentageSlideResults$);
  selectedWrongPercentageSlideResults$ = this.store.select(FlatStates.TrainingSharedState.selectedWrongPercentageSlideResults$);
  selectedNodeIndex$ = this.store.select(FlatStates.TrainingSharedState.selectedNodeIndex$);
  selectedSlidePercentage$ = this.store.select(FlatStates.TrainingSharedState.selectedSlidePercentage$);
  previousSlideNode$ = this.store.select(FlatStates.TrainingSharedState.previousSlideNode$);
  previousSlideUrl$ = this.store.select(FlatStates.TrainingSharedState.previousSlideUrl$);
  nextSlideNode$ = this.store.select(FlatStates.TrainingSharedState.nextSlideNode$);
  nextSlideUrl$ = this.store.select(FlatStates.TrainingSharedState.nextSlideUrl$);
  isTrainingSetupCorrectly$ = this.store.select(FlatStates.TrainingSharedState.isTrainingSetupCorrectly$);
  trainingSetupIncorrectlyUrl$ = this.store.select(FlatStates.TrainingSharedState.trainingSetupIncorrectlyUrl$);
  slideSelectionAvailable$ = this.store.select(FlatStates.TrainingSharedState.slideSelectionAvailable$);
  quizActive$ = this.store.select(FlatStates.NavigationControlState.quizActive$);


  quizStartData$ = this.store.select(FlatStates.TrainingSharedState.quizStartData$);

  isTraining$ = this.store.select(FlatStates.NodeEntityState.isTraining$);
  currentSelectedSlidePage$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePage$);
  currentSelectedSlideQuestions$ = this.store.select(FlatStates.QuestionEntityState.currentSelectedSlideQuestions$);
  
  typedQuestionChoicesOrder$ = this.store.select(FlatStates.TrainingSharedState.typedQuestionChoicesOrder$);

  //</editor-fold>

  //<editor-fold desc="Lazy Selectors">

  currentAvailableSlidePagesByChapterId$ = (chapterId: any) => this.store.select(FlatStates.TrainingSharedState.currentAvailableSlidePagesByChapterId$).pipe(map(filterFn => filterFn(chapterId as unknown as string)));

  node$ = (id: any) => this.store.select(FlatStates.NodeEntityState.entity$).pipe(map(filterFn => filterFn(id as unknown as string)));
  page$ = (id: any) => this.store.select(FlatStates.PageEntityState.entity$).pipe(map(filterFn => filterFn(id as unknown as string)));
  choicesByIds$ = (ids: any) => this.store.select(FlatStates.ChoiceEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids as unknown as string[])));
  currentSlideResultByQuestionId$ = (questionId: string) => this.store.select(FlatStates.TrainingSharedState.currentSlideResultByQuestionId$).pipe(map(filterFn => filterFn(questionId)));
  slideUrlByNode$ = (slideNode: FlatModels.NodeEntityModels.SlideNode) => this.store.select(FlatStates.TrainingSharedState.slideUrlByNode$).pipe(map(filterFn => filterFn(slideNode)));

  selectedQuestionIndex$ = (questionId: string) => this.store.select(FlatStates.TrainingSharedState.selectedQuestionIndex$).pipe(map(filterFn => filterFn(questionId)));
  //</editor-fold>

  //<editor-fold desc="Helpers">

  helperListCharacter(numberingType: FlatModels.QuestionEntityModels.NumberingType | undefined,
                      index: number): string {
    switch (numberingType) {
      case FlatModels.QuestionEntityModels.NumberingType.LETTER:
        return this.toLetterEnumeration(index) + '.';
      case FlatModels.QuestionEntityModels.NumberingType.ROMAN:
        return this.toRomanEnumeration(index) + '.';
      case FlatModels.QuestionEntityModels.NumberingType.NONE:
        return '';
      default:
        return (index + 1).toString() + '.';
    }
  }

  private toLetterEnumeration(index: number) {
    const offset = 65;
    const base = 26;
    let div = Math.floor(index / base);
    let mod = index % base;
    let value = String.fromCharCode(offset + mod);
    
    while (div !== 0) {
      div -= 1;
      mod = (div % base);
      div = Math.floor(div / base);
      value = String.fromCharCode(offset + mod) + value;
    }
    return value;
  }

  private toRomanEnumeration(index: number) {
    let num = index + 1;
    const lookup = [['M', 1000], ['CM', 900], ['D', 500], ['CD', 400], ['C', 100], ['XC', 90], ['L', 50], ['XL', 40], ['X', 10], ['IX', 9],['V',5],['IV',4],['I',1]] as const;
    let roman = '';
    for (const v of lookup) {
      while(num >= v[1]) {
        roman += v[0];
        num -= v[1];
      }
    }
    return roman;
  }

  //</editor-fold>

  //<editor-fold desc="Requests">

  requestTrainingNodesByCurrentSubject() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.GetTrainingNodesByCurrentSubject());
  }

  requestTrainingNodesBySelectedSlides() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.GetTrainingNodesBySelectedSlides());
  }

  requestToggleTrainingSelection(slides: string[], event: any) {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.ToggleTrainingSelection(slides, event.target.checked));
  }

  requestReset() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.Reset());
  }

  requestStartTraining() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.SetupTraining());
  }

  requestStartCase() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.SetupCase());
  }

  requestUpdateSlideResult(slideResult: FlatModels.TrainingSharedModels.TrainingResultType) {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.UpdateSlideResult(slideResult));
  }

  requestSetShowFeedback(slideResult: FlatModels.TrainingSharedModels.TrainingResultType,
                         slideId: string,
                         question: FlatModels.QuestionEntityModels.QuestionEntityType,
                         isCorrect: boolean) {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.SetShowFeedback(slideResult, question)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.TrainingSharedActions.TrackQuizAnswer(slideId, question, isCorrect))
      ),
    );
  }

  requestToggleShowExplanation(slideResult: FlatModels.TrainingSharedModels.TrainingResultType) {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.ToggleShowExplanation(slideResult));
  }

  requestGoToStart() {
    return this.store.dispatch(new FlatActions.TrainingSharedActions.GoToStart());
  }

  requestGoToSlideByIndex(eventTarget: EventTarget,
                          currentIndex: number,
                          selectedSlides: Set<string>,
                          selectedSlideNodes: FlatModels.NodeEntityModels.SlideNode[],
                          slideResultsAvailable: {[id: string]: boolean},
                          queryParams: Params) {
    // Get Index
    const target = eventTarget as HTMLInputElement;
    let index = Number.parseInt(target.value, 10);
    if (!isNaN(index)) {
      index -= 1;
      if (index >= 0 && index < selectedSlides.size) {
        const selectedSlideId = Array.from(selectedSlides)[index];
        const selectedSlideNode = selectedSlideNodes.find(sn => sn.id === selectedSlideId);
        if (selectedSlideNode) {
          if (slideResultsAvailable[selectedSlideNode.page as unknown as string]) {
            return this.store.dispatch(new FlatActions.TrainingSharedActions.GoToSlideByNode(selectedSlideNode, queryParams));
          }
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.node_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatFacades.TrainingSharedFacade.requestGoToSlideByIndex"
          );
        }
      }
    }

    // Reset Index Value
    target.value = (currentIndex + 1).toString();
    return false;
  }
  
  requestGenerateCustomEnumeration(numberingType: FlatModels.QuestionEntityModels.NumberingType,
                                   children: string[]) {
    // Get Data
    const typedNodes = this.store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const nodes = children.reduce((arr, id) => {
      const entity = typedNodes[id];
      if (entity && !entity.hidden) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
    return forkJoin(nodes.map(node => this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataByPageId(node.page)))).pipe(
      mergeMap(() => {
        const typedPages = this.store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
        const pages = nodes.sort((a, b) => a.position - b.position).reduce((arr, node) => {
          const entity = typedPages[node.page] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as (FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training)[]);
        
        // Update Questions
        let index = 0;
        const typedQuestions = this.store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);
        const questions = pages.reduce<FlatModels.QuestionEntityModels.QuestionEntityType[]>((arr, page) => {
          // Skip Training Pages
          if (page.type === 'Training') return arr;
          arr.push(...page.questions
            .map(question => structuredClone(typedQuestions[question]))
            .filter(question => question)
            .sort((a, b) => a.order - b.order)
            .map((question) => {
              question.customEnumeration = this.helperListCharacter(numberingType, index++);
              return question;
            }));
          return arr;
        }, [])
        return forkJoin(questions.map(question => this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.custom_enumertation_generated',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    )
  }

  //</editor-fold>

}
